import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import 'bootstrap/dist/css/bootstrap.min.css'
import './index.css'
import './i18n'
import xrayTracer from './utils/xray-tracing'
import { analyticsService } from './services/analyticsService'
import { startVersionChecker } from './utils/versionChecker'
import { notifyUpdateAvailable, notifyUpdateReady, setSWRegistration } from './hooks/useAppUpdate'
import { isStandalone } from './utils/platform'

// Initialize monitoring services
xrayTracer.startSegment('Application Initialization');
// CloudWatch RUM initializes automatically

const appLoadStart = performance.now()

// Register Service Worker for PWA (disabled in preview mode to avoid caching issues)
const isPreviewMode = import.meta.env.VITE_BASE_PATH && import.meta.env.VITE_BASE_PATH.includes('/preview/');
if (!isPreviewMode) {
  import('virtual:pwa-register').then(({ registerSW }) => {
    registerSW({
      onOfflineReady() {
        // App cached for offline use
      },
      onRegisteredSW(_swUrl, registration) {
        if (registration) {
          xrayTracer.addSubsegment('Service Worker Registration', 100, { success: true })
          setSWRegistration(registration)

          const hadPreviousController = !!navigator.serviceWorker.controller

          if (hadPreviousController) {
            const trackInstalling = (sw: ServiceWorker) => {
              sw.addEventListener('statechange', () => {
                if (sw.state === 'installed') {
                  notifyUpdateReady(false)
                }
              })
            }

            registration.addEventListener('updatefound', () => {
              notifyUpdateAvailable()
              if (registration.installing) {
                trackInstalling(registration.installing)
              }
            })
          }
        }
      },
      onRegisterError(error) {
        xrayTracer.addSubsegment('Service Worker Registration', 100, {
          success: false,
          error: error.message
        })
      }
    })
  })
} else {
  // Unregister any existing service workers in preview mode
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(registrations => {
      registrations.forEach(registration => {
        registration.unregister();
      });
    });
  }
}

// Runtime deploy-detection safety net: poll /buildInfo.json and reload if the server has a newer build.
// Guards against stuck service workers that pinned an old bundle across a deploy.
startVersionChecker()

const rootElement = document.getElementById('root');
if (rootElement) {
  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  )

  const isPwa = isStandalone()
  const swActive = 'serviceWorker' in navigator && !!navigator.serviceWorker.controller
  analyticsService.track('system.app.loaded', {
    load_time_ms: Math.round(performance.now() - appLoadStart),
    is_pwa: isPwa,
    service_worker_active: swActive
  })
}
